import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import clinicalNoteService from './clinicalNoteService.js';
import providerSignatureService from './providerSignatureService.js';

const MAX_OVERRIDE_REASON_LENGTH = 2000;

/**
 * Normalize application roles.
 *
 * Supports both:
 *
 * MANAGER
 * ROLE_MANAGER
 */
function normalizeRole(role) {
  if (!role) {
    return '';
  }

  const normalized =
    String(role)
      .trim()
      .toUpperCase();

  if (
    normalized.startsWith(
      'ROLE_'
    )
  ) {
    return normalized.substring(5);
  }

  return normalized;
}

/**
 * Read the current role from localStorage when a role was not
 * explicitly passed by the parent page.
 */
function getStoredRole() {
  if (
    typeof window ===
    'undefined'
  ) {
    return '';
  }

  return normalizeRole(
    window.localStorage.getItem(
      'role'
    )
  );
}

/**
 * Convert nullable values to safe display text.
 */
function displayValue(
  value,
  fallback = '—'
) {
  if (
    value === null ||
    value === undefined ||
    String(value).trim() === ''
  ) {
    return fallback;
  }

  return String(value);
}

/**
 * Copy only the fields permitted by
 * ClinicalNoteSignatureDisplayResponse.
 *
 * This deliberately discards any additional fields that might be
 * present in an older/internal signing response.
 */
function sanitizeSignatureDisplay(
  source
) {
  if (!source) {
    return null;
  }

  return {
    clinicalNoteSignatureId:
      source.clinicalNoteSignatureId ??
      null,

    clinicalNoteId:
      source.clinicalNoteId ??
      null,

    providerId:
      source.providerId ??
      null,

    providerName:
      source.providerName ??
      null,

    credentials:
      source.credentials ??
      null,

    licenseNumber:
      source.licenseNumber ??
      null,

    licenseState:
      source.licenseState ??
      null,

    signatureImageAvailable:
      source.signatureImageAvailable ===
      true,

    signaturePrefixText:
      source.signaturePrefixText ??
      null,

    requiredSignatureText:
      source.requiredSignatureText ??
      null,

    signatureDate:
      source.signatureDate ??
      null,
  };
}

/**
 * Format one reusable provider signature for a dropdown.
 */
function formatProviderSignature(
  signature
) {
  if (!signature) {
    return '';
  }

  const name =
    displayValue(
      signature.providerName,
      'Unnamed Provider'
    );

  const credentials =
    signature.credentials
      ? `, ${signature.credentials}`
      : '';

  const licenseParts =
    [
      signature.licenseState,
      signature.licenseNumber,
    ].filter(Boolean);

  const license =
    licenseParts.length > 0
      ? ` — License ${licenseParts.join(' ')}`
      : '';

  return `${name}${credentials}${license}`;
}

/**
 * Safe read-only display for a permanent clinical-note signature.
 */
function PermanentSignatureDisplay({
  signature,
}) {
  if (!signature) {
    return null;
  }

  return (
    <section
      style={{
        border:
          '1px solid #a8d5b0',
        borderRadius: 9,
        overflow: 'hidden',
        background: '#f6fbf7',
      }}
    >
      <div
        style={{
          padding: '10px 12px',
          background: '#e7f6ea',
          borderBottom:
            '1px solid #b8d9bf',
        }}
      >
        <strong>
          Permanent Clinical Signature
        </strong>

        <div
          style={{
            marginTop: 3,
            fontSize: 12,
            color: '#276738',
          }}
        >
          This signature is part of the
          finalized clinical-note
          snapshot.
        </div>
      </div>

      <div
        style={{
          padding: 12,
        }}
      >
        {signature
          .signaturePrefixText && (
          <div
            style={{
              marginBottom: 5,
              fontSize: 12,
              color: '#666',
            }}
          >
            {
              signature
                .signaturePrefixText
            }
          </div>
        )}

        <div
          style={{
            fontSize: 17,
            fontWeight: 700,
          }}
        >
          {displayValue(
            signature.providerName,
            'Provider'
          )}

          {signature.credentials
            ? `, ${signature.credentials}`
            : ''}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit, minmax(170px, 1fr))',
            gap: 9,
            marginTop: 11,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                textTransform:
                  'uppercase',
                color: '#777',
                fontWeight: 700,
              }}
            >
              License
            </div>

            <div
              style={{
                marginTop: 3,
                fontSize: 13,
              }}
            >
              {signature.licenseState
                ? `${signature.licenseState} `
                : ''}
              {displayValue(
                signature.licenseNumber
              )}
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: 10,
                textTransform:
                  'uppercase',
                color: '#777',
                fontWeight: 700,
              }}
            >
              Signature Date
            </div>

            <div
              style={{
                marginTop: 3,
                fontSize: 13,
              }}
            >
              {displayValue(
                signature.signatureDate
              )}
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: 10,
                textTransform:
                  'uppercase',
                color: '#777',
                fontWeight: 700,
              }}
            >
              Signature Image
            </div>

            <div
              style={{
                marginTop: 3,
                fontSize: 13,
              }}
            >
              {signature
                .signatureImageAvailable
                ? 'Available'
                : 'Not available'}
            </div>
          </div>
        </div>

        {signature
          .requiredSignatureText && (
          <div
            style={{
              marginTop: 11,
              borderTop:
                '1px solid #d7e8da',
              paddingTop: 9,
              whiteSpace: 'pre-wrap',
              fontSize: 12,
              lineHeight: 1.45,
            }}
          >
            {
              signature
                .requiredSignatureText
            }
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Permanent clinical-note signing interface.
 *
 * Normal signing:
 *
 * - note must be READY_FOR_REVIEW
 * - backend validation must return canSign=true
 * - signature is selected from the assigned provider's active
 *   ProviderSignatureMain records
 *
 * Manager/Admin override:
 *
 * - all active provider signatures may be viewed
 * - selecting a different provider requires an explanation
 * - backend independently verifies the authenticated role
 *
 * Signing is permanent and locks normal note editing.
 */
export default function ClinicalNoteSignaturePanel({
  clinicalNoteId,
  enabled = true,
  disabled = false,
  userRole = null,
  refreshToken = 0,
  onSigned,
  onSignatureChange,
  onVersionChange,
}) {
  const [
    note,
    setNote,
  ] = useState(null);

  const [
    validation,
    setValidation,
  ] = useState(null);

  const [
    signature,
    setSignature,
  ] = useState(null);

  const [
    assignedSignatures,
    setAssignedSignatures,
  ] = useState([]);

  const [
    overrideSignatures,
    setOverrideSignatures,
  ] = useState([]);

  const [
    overrideMode,
    setOverrideMode,
  ] = useState(false);

  const [
    overrideSignaturesLoaded,
    setOverrideSignaturesLoaded,
  ] = useState(false);

  const [
    selectedSignatureId,
    setSelectedSignatureId,
  ] = useState('');

  const [
    managerOverrideReason,
    setManagerOverrideReason,
  ] = useState('');

  const [
    permanentConfirmation,
    setPermanentConfirmation,
  ] = useState(false);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    overrideLoading,
    setOverrideLoading,
  ] = useState(false);

  const [
    signing,
    setSigning,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  const [
    successMessage,
    setSuccessMessage,
  ] = useState('');

  const effectiveRole =
    normalizeRole(
      userRole
    ) ||
    getStoredRole();

  const canUseManagerOverride =
    effectiveRole === 'ADMIN' ||
    effectiveRole === 'MANAGER';

  const roleMaySign =
    effectiveRole === 'ADMIN' ||
    effectiveRole === 'MANAGER' ||
    effectiveRole === 'DOCTOR';

  /**
   * Notify the parent when a newer ClinicalNoteMain version is
   * available.
   */
  const publishNoteVersion =
    useCallback(
      (
        nextNote
      ) => {
        if (
          nextNote?.entityVersion !==
            null &&
          nextNote?.entityVersion !==
            undefined &&
          typeof onVersionChange ===
            'function'
        ) {
          onVersionChange(
            nextNote.entityVersion,
            nextNote
          );
        }
      },
      [onVersionChange]
    );

  /**
   * Load the current note, permanent signature, backend validation,
   * and assigned-provider signatures.
   */
  const loadSigningState =
    useCallback(
      async () => {
        if (
          !enabled ||
          !clinicalNoteId
        ) {
          return;
        }

        setLoading(true);
        setError('');

        try {
          const [
            noteResponse,
            signatureResponse,
          ] =
            await Promise.all([
              clinicalNoteService
                .getClinicalNote(
                  clinicalNoteId
                ),

              clinicalNoteService
                .getSignature(
                  clinicalNoteId
                ),
            ]);

          setNote(
            noteResponse
          );

          publishNoteVersion(
            noteResponse
          );

          const safeSignature =
            sanitizeSignatureDisplay(
              signatureResponse
            );

          setSignature(
            safeSignature
          );

          if (
            typeof onSignatureChange ===
            'function'
          ) {
            onSignatureChange(
              safeSignature
            );
          }

          /*
           * Once signed, no reusable signature-selection data is
           * required anymore.
           */
          if (
            noteResponse?.status ===
              'SIGNED' ||
            safeSignature
              ?.clinicalNoteSignatureId
          ) {
            setValidation(null);
            setAssignedSignatures([]);
            setSelectedSignatureId('');
            setOverrideMode(false);
            setManagerOverrideReason('');
            setPermanentConfirmation(
              false
            );

            return;
          }

          /*
           * Signature selection is only meaningful to roles that can
           * perform the signing action.
           */
          if (!roleMaySign) {
            setValidation(null);
            setAssignedSignatures([]);
            return;
          }

          if (
            noteResponse?.status !==
            'READY_FOR_REVIEW'
          ) {
            setValidation(null);
            setAssignedSignatures([]);
            return;
          }

          const providerId =
            noteResponse?.providerId;

          if (
            providerId === null ||
            providerId === undefined ||
            Number(providerId) <= 0
          ) {
            setAssignedSignatures([]);

            setError(
              'The clinical note does not have a valid assigned provider for signing.'
            );

            return;
          }

          const [
            validationResponse,
            signatureList,
          ] =
            await Promise.all([
              clinicalNoteService
                .validateClinicalNote(
                  clinicalNoteId
                ),

              providerSignatureService
                .getActiveProviderSignaturesByProvider(
                  providerId
                ),
            ]);

          setValidation(
            validationResponse
          );

          setAssignedSignatures(
            Array.isArray(
              signatureList
            )
              ? signatureList
              : []
          );
        } catch (loadError) {
          setError(
            loadError?.message ||
              'Clinical-note signing information could not be loaded.'
          );
        } finally {
          setLoading(false);
        }
      },
      [
        clinicalNoteId,
        enabled,
        publishNoteVersion,
        roleMaySign,
        onSignatureChange,
      ]
    );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    loadSigningState();
  }, [
    loadSigningState,
    refreshToken,
    enabled,
  ]);

  /**
   * Signatures visible in the current selection mode.
   */
  const availableSignatures =
    overrideMode
      ? overrideSignatures
      : assignedSignatures;

  const selectedSignature =
    useMemo(
      () =>
        availableSignatures.find(
          (item) =>
            String(
              item.providerSignatureId
            ) ===
            String(
              selectedSignatureId
            )
        ) || null,
      [
        availableSignatures,
        selectedSignatureId,
      ]
    );

  /**
   * A provider mismatch is the condition that actually requires the
   * Manager/Admin override reason.
   */
  const providerMismatch =
    Boolean(
      selectedSignature &&
      note?.providerId !==
        null &&
      note?.providerId !==
        undefined &&
      Number(
        selectedSignature.providerId
      ) !==
        Number(
          note.providerId
        )
    );

  const overrideReasonLength =
    managerOverrideReason.length;

  /**
   * Enter or leave Manager/Admin override selection.
   */
  async function changeOverrideMode(
    checked
  ) {
    if (
      !canUseManagerOverride ||
      signing
    ) {
      return;
    }

    setSelectedSignatureId('');
    setManagerOverrideReason('');
    setPermanentConfirmation(false);
    setError('');

    if (!checked) {
      setOverrideMode(false);
      return;
    }

    setOverrideMode(true);

    if (
      overrideSignaturesLoaded
    ) {
      return;
    }

    setOverrideLoading(true);

    try {
      const response =
        await providerSignatureService
          .getActiveProviderSignatures();

      setOverrideSignatures(
        Array.isArray(response)
          ? response
          : []
      );

      setOverrideSignaturesLoaded(
        true
      );
    } catch (loadError) {
      setOverrideMode(false);

      setError(
        loadError?.message ||
          'Provider signatures could not be loaded for Manager/Admin override.'
      );
    } finally {
      setOverrideLoading(false);
    }
  }

  /**
   * Update reusable signature selection.
   */
  function changeSelectedSignature(
    value
  ) {
    setSelectedSignatureId(
      value
    );

    /*
     * A different signature requires a new explicit permanent-sign
     * confirmation.
     */
    setPermanentConfirmation(
      false
    );

    setManagerOverrideReason(
      ''
    );

    setError('');
    setSuccessMessage('');
  }

  /**
   * Permanently sign the clinical note.
   */
  async function signClinicalNote() {
    if (
      disabled ||
      signing ||
      !roleMaySign
    ) {
      return;
    }

    if (!selectedSignature) {
      setError(
        'Select a provider signature before permanently signing the clinical note.'
      );

      return;
    }

    if (
      providerMismatch &&
      !canUseManagerOverride
    ) {
      setError(
        'The selected signature does not belong to the provider assigned to this clinical note.'
      );

      return;
    }

    const normalizedOverrideReason =
      managerOverrideReason.trim();

    if (
      providerMismatch &&
      !normalizedOverrideReason
    ) {
      setError(
        'A Manager/Admin override reason is required when signing with a different provider.'
      );

      return;
    }

    if (
      normalizedOverrideReason.length >
      MAX_OVERRIDE_REASON_LENGTH
    ) {
      setError(
        'Manager override reason cannot exceed 2000 characters.'
      );

      return;
    }

    if (!permanentConfirmation) {
      setError(
        'Confirm that you understand permanent signing will lock this clinical note.'
      );

      return;
    }

    setSigning(true);
    setError('');
    setSuccessMessage('');

    let signSucceeded =
      false;

    try {
      /*
       * Always perform a fresh validation immediately before the
       * irreversible action.
       *
       * Its entityVersion identifies the exact note state the user
       * is attempting to sign.
       */
      const preflight =
        await clinicalNoteService
          .validateClinicalNote(
            clinicalNoteId
          );

      setValidation(
        preflight
      );

      if (
        preflight?.status !==
        'READY_FOR_REVIEW'
      ) {
        throw new Error(
          'The clinical note must be Ready For Review before it can be signed.'
        );
      }

      if (
        preflight?.canSign !==
        true
      ) {
        if (
          preflight
            ?.hasUnresolvedWarnings
        ) {
          throw new Error(
            'Clinical warnings must be corrected or acknowledged before permanent signing.'
          );
        }

        const blockingCount =
          Number(
            preflight
              ?.blockingIssueCount ??
              0
          );

        if (blockingCount === 1) {
          throw new Error(
            'The clinical note still has 1 blocking validation issue that must be corrected before signing.'
          );
        }

        if (blockingCount > 1) {
          throw new Error(
            `The clinical note still has ${blockingCount} blocking validation issues that must be corrected before signing.`
          );
        }

        throw new Error(
          'The backend does not currently allow this clinical note to be signed.'
        );
      }

      const expectedVersion =
        preflight?.entityVersion;

      if (
        expectedVersion ===
          null ||
        expectedVersion ===
          undefined
      ) {
        throw new Error(
          'The current clinical note version could not be determined.'
        );
      }

      /*
       * The frontend sends only:
       *
       * - providerSignatureId
       * - Manager/Admin override reason when actually needed
       * - expected optimistic-lock version
       *
       * Provider identity, license information, signature image,
       * signature date, and signing staff identity are backend-owned.
       */
      const signResponse =
        await clinicalNoteService
          .signClinicalNote(
            clinicalNoteId,
            {
              providerSignatureId:
                Number(
                  selectedSignature
                    .providerSignatureId
                ),

              managerOverrideReason:
                providerMismatch
                  ? normalizedOverrideReason
                  : null,

              expectedVersion,
            }
          );

      signSucceeded =
        true;

      /*
       * Do not expose any internal fields that might exist in a
       * legacy signing response. Copy only the safe display fields.
       */
      const safeResponse =
        sanitizeSignatureDisplay(
          signResponse
        );

      if (safeResponse) {
        setSignature(
          safeResponse
        );
      }

      /*
       * Reload the official display endpoint and note header.
       *
       * These reads happen after the signing transaction so the UI
       * receives the permanent snapshot plus SIGNED/locked note state.
       */
      const results =
        await Promise.allSettled([
          clinicalNoteService
            .getSignature(
              clinicalNoteId
            ),

          clinicalNoteService
            .getClinicalNote(
              clinicalNoteId
            ),
        ]);

      let finalSignature =
        safeResponse;

      let finalNote =
        note;

      if (
        results[0].status ===
        'fulfilled'
      ) {
        finalSignature =
          sanitizeSignatureDisplay(
            results[0].value
          );

        setSignature(
          finalSignature
        );
      }

      if (
        results[1].status ===
        'fulfilled'
      ) {
        finalNote =
          results[1].value;

        setNote(
          finalNote
        );

        publishNoteVersion(
          finalNote
        );
      } else {
        /*
         * Signing already succeeded.
         *
         * Never present a post-sign refresh problem as though the
         * irreversible signing action itself failed.
         */
        setNote(
          (current) => ({
            ...current,
            status: 'SIGNED',
            locked: true,
          })
        );
      }

      setSelectedSignatureId('');
      setManagerOverrideReason('');
      setPermanentConfirmation(false);
      setOverrideMode(false);

      setSuccessMessage(
        results[0].status ===
          'fulfilled' &&
        results[1].status ===
          'fulfilled'
          ? 'Clinical note permanently signed and locked.'
          : 'Clinical note was permanently signed. Some display information could not be refreshed automatically.'
      );

      if (
        typeof onSignatureChange ===
        'function'
      ) {
        onSignatureChange(
          finalSignature
        );
      }

      if (
        typeof onSigned ===
        'function'
      ) {
        onSigned(
          finalSignature,
          finalNote
        );
      }
    } catch (signError) {
      /*
       * If the POST already succeeded, never encourage the user to
       * repeat the permanent action because a later display refresh
       * failed.
       */
      if (signSucceeded) {
        setSuccessMessage(
          'Clinical note was permanently signed, but the signed display could not be completely refreshed.'
        );

        return;
      }

      setError(
        signError?.message ||
          'Clinical note could not be permanently signed.'
      );

      /*
       * Refresh backend state after an optimistic-lock or workflow
       * conflict.
       */
      try {
        const [
          refreshedNote,
          refreshedValidation,
        ] =
          await Promise.all([
            clinicalNoteService
              .getClinicalNote(
                clinicalNoteId
              ),

            clinicalNoteService
              .validateClinicalNote(
                clinicalNoteId
              ),
          ]);

        setNote(
          refreshedNote
        );

        setValidation(
          refreshedValidation
        );

        publishNoteVersion(
          refreshedNote
        );

        /*
         * If another user completed the signature while this request
         * was stale, load the permanent snapshot instead of allowing
         * another signing attempt.
         */
        if (
          refreshedNote?.status ===
          'SIGNED'
        ) {
          try {
            const refreshedSignature =
              sanitizeSignatureDisplay(
                await clinicalNoteService
                  .getSignature(
                    clinicalNoteId
                  )
              );

            setSignature(
              refreshedSignature
            );

            if (
              typeof onSignatureChange ===
              'function'
            ) {
              onSignatureChange(
                refreshedSignature
              );
            }
          } catch {
            /*
             * The note state itself is enough to prevent another
             * signing attempt.
             */
          }
        }
      } catch {
        /*
         * Preserve the original signing error.
         */
      }
    } finally {
      setSigning(false);
    }
  }

  const noteStatus =
    note?.status || '';

  const alreadySigned =
    noteStatus === 'SIGNED' ||
    Boolean(
      signature
        ?.clinicalNoteSignatureId
    );

  const canSignAccordingToValidation =
    validation?.canSign ===
    true;

  const assignedProviderHasSignatures =
    assignedSignatures.length >
    0;

  const overrideReasonValid =
    !providerMismatch ||
    (
      canUseManagerOverride &&
      managerOverrideReason
        .trim()
        .length > 0 &&
      managerOverrideReason
        .trim()
        .length <=
        MAX_OVERRIDE_REASON_LENGTH
    );

  const canPressSign =
    !disabled &&
    !signing &&
    roleMaySign &&
    noteStatus ===
      'READY_FOR_REVIEW' &&
    canSignAccordingToValidation &&
    Boolean(
      selectedSignature
    ) &&
    overrideReasonValid &&
    permanentConfirmation;

  if (!enabled) {
    return null;
  }

  return (
    <section
      style={{
        border:
          '1px solid #d7d7d7',
        borderRadius: 10,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* =====================================================
          HEADER
          ===================================================== */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding:
            '13px 16px',
          background:
            '#f7f8fa',
          borderBottom:
            '1px solid #ddd',
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 17,
            }}
          >
            Clinical Signature
          </h3>

          <div
            style={{
              marginTop: 3,
              fontSize: 12,
              color: '#666',
            }}
          >
            Permanent provider signature
            and final clinical-note
            locking.
          </div>
        </div>

        <button
          type="button"
          disabled={
            loading ||
            signing
          }
          onClick={
            loadSigningState
          }
        >
          {loading
            ? 'Refreshing…'
            : 'Refresh Signing Status'}
        </button>
      </div>

      <div
        style={{
          padding: 16,
        }}
      >
        {/* ===================================================
            ERROR
            =================================================== */}
        {error && (
          <div
            role="alert"
            style={{
              marginBottom: 14,
              border:
                '1px solid #b42318',
              borderRadius: 8,
              background:
                '#fff5f5',
              padding: 11,
            }}
          >
            <strong>
              Signing action requires
              attention.
            </strong>

            <div
              style={{
                marginTop: 4,
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              {error}
            </div>
          </div>
        )}

        {/* ===================================================
            SUCCESS
            =================================================== */}
        {successMessage && (
          <div
            role="status"
            style={{
              marginBottom: 14,
              border:
                '1px solid #a8d5b0',
              borderRadius: 8,
              background:
                '#f4fbf5',
              padding: 11,
              color: '#276738',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {successMessage}
          </div>
        )}

        {/* ===================================================
            LOADING
            =================================================== */}
        {loading &&
          !note && (
          <div
            style={{
              color: '#666',
              fontSize: 13,
            }}
          >
            Loading clinical signing
            information…
          </div>
        )}

        {/* ===================================================
            PERMANENT SIGNED SNAPSHOT
            =================================================== */}
        {alreadySigned && (
          <>
            <div
              style={{
                marginBottom: 12,
                border:
                  '1px solid #a8d5b0',
                borderRadius: 8,
                background:
                  '#f4fbf5',
                padding: 11,
                color: '#276738',
              }}
            >
              <strong>
                This clinical note is
                permanently signed and
                locked.
              </strong>

              <div
                style={{
                  marginTop: 4,
                  fontSize: 12,
                  lineHeight: 1.45,
                }}
              >
                Normal clinical editing
                and normal workflow
                changes are no longer
                available.
              </div>
            </div>

            {signature ? (
              <PermanentSignatureDisplay
                signature={
                  signature
                }
              />
            ) : (
              <div
                style={{
                  border:
                    '1px solid #d6a100',
                  borderRadius: 8,
                  background:
                    '#fff8df',
                  padding: 11,
                  fontSize: 13,
                }}
              >
                The note is marked
                Signed, but its safe
                signature display could
                not currently be loaded.
              </div>
            )}
          </>
        )}

        {/* ===================================================
            ROLE RESTRICTION
            =================================================== */}
        {!alreadySigned &&
          note &&
          !roleMaySign && (
          <div
            style={{
              border:
                '1px solid #ddd',
              borderRadius: 8,
              background:
                '#fafafa',
              padding: 11,
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            Your current role may view
            clinical signing
            information but cannot
            permanently sign a clinical
            note.
          </div>
        )}

        {/* ===================================================
            WRONG WORKFLOW STATE
            =================================================== */}
        {!alreadySigned &&
          roleMaySign &&
          note &&
          noteStatus !==
            'READY_FOR_REVIEW' && (
          <div
            style={{
              border:
                '1px solid #ddd',
              borderRadius: 8,
              background:
                '#fafafa',
              padding: 11,
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            This note must first be
            moved to{' '}
            <strong>
              Ready For Review
            </strong>{' '}
            before permanent signing is
            available.

            {noteStatus && (
              <>
                {' '}
                Current status:{' '}
                <strong>
                  {noteStatus}
                </strong>
                .
              </>
            )}
          </div>
        )}

        {/* ===================================================
            SIGNING WORKSPACE
            =================================================== */}
        {!alreadySigned &&
          roleMaySign &&
          noteStatus ===
            'READY_FOR_REVIEW' && (
          <>
            {/* ===============================================
                READINESS
                =============================================== */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns:
                  'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 9,
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  border:
                    canSignAccordingToValidation
                      ? '1px solid #a8d5b0'
                      : '1px solid #d6a100',
                  borderRadius: 8,
                  background:
                    canSignAccordingToValidation
                      ? '#f4fbf5'
                      : '#fff8df',
                  padding: 10,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    textTransform:
                      'uppercase',
                    fontWeight: 700,
                    color: '#777',
                  }}
                >
                  Backend Signing
                  Validation
                </div>

                <div
                  style={{
                    marginTop: 4,
                    fontWeight: 700,
                  }}
                >
                  {canSignAccordingToValidation
                    ? 'Ready To Sign'
                    : 'Not Ready'}
                </div>
              </div>

              <div
                style={{
                  border:
                    '1px solid #ddd',
                  borderRadius: 8,
                  padding: 10,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    textTransform:
                      'uppercase',
                    fontWeight: 700,
                    color: '#777',
                  }}
                >
                  Assigned Provider ID
                </div>

                <div
                  style={{
                    marginTop: 4,
                    fontWeight: 700,
                  }}
                >
                  {displayValue(
                    note.providerId
                  )}
                </div>
              </div>

              <div
                style={{
                  border:
                    '1px solid #ddd',
                  borderRadius: 8,
                  padding: 10,
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    textTransform:
                      'uppercase',
                    fontWeight: 700,
                    color: '#777',
                  }}
                >
                  Signature Date
                </div>

                <div
                  style={{
                    marginTop: 4,
                    fontWeight: 700,
                  }}
                >
                  {displayValue(
                    note.noteDate
                  )}
                </div>

                <div
                  style={{
                    marginTop: 3,
                    fontSize: 10,
                    color: '#777',
                  }}
                >
                  Set automatically from
                  the clinical note date.
                </div>
              </div>
            </div>

            {!canSignAccordingToValidation && (
              <div
                style={{
                  marginBottom: 14,
                  border:
                    '1px solid #d6a100',
                  borderRadius: 8,
                  background:
                    '#fff8df',
                  padding: 10,
                  fontSize: 12,
                  lineHeight: 1.45,
                }}
              >
                The backend currently
                does not allow permanent
                signing. Review the
                Clinical Note Validation
                and Clinical Warnings
                panels before
                continuing.
              </div>
            )}

            {/* ===============================================
                SIGNATURE SELECTOR
                =============================================== */}
            <section
              style={{
                border:
                  '1px solid #ddd',
                borderRadius: 9,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  padding:
                    '10px 12px',
                  background:
                    '#f7f8fa',
                  borderBottom:
                    '1px solid #ddd',
                }}
              >
                <strong>
                  Provider Signature
                </strong>

                <div
                  style={{
                    marginTop: 3,
                    fontSize: 12,
                    color: '#666',
                  }}
                >
                  {overrideMode
                    ? 'Manager/Admin override mode: all active provider signatures are available.'
                    : 'Only active signatures belonging to the provider assigned to this clinical note are shown.'}
                </div>
              </div>

              <div
                style={{
                  padding: 12,
                }}
              >
                {!overrideMode &&
                  !assignedProviderHasSignatures && (
                  <div
                    style={{
                      marginBottom: 11,
                      border:
                        '1px solid #d6a100',
                      borderRadius: 7,
                      background:
                        '#fff8df',
                      padding: 9,
                      fontSize: 12,
                      lineHeight: 1.45,
                    }}
                  >
                    No active reusable
                    signature is
                    configured for the
                    provider assigned to
                    this clinical note.
                  </div>
                )}

                <label
                  htmlFor="clinical-provider-signature"
                  style={{
                    display: 'block',
                    marginBottom: 5,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Select Signature
                  <span
                    aria-hidden="true"
                    style={{
                      marginLeft: 4,
                      color: '#b42318',
                    }}
                  >
                    *
                  </span>
                </label>

                <select
                  id="clinical-provider-signature"
                  value={
                    selectedSignatureId
                  }
                  disabled={
                    disabled ||
                    signing ||
                    overrideLoading ||
                    availableSignatures
                      .length === 0
                  }
                  onChange={(event) =>
                    changeSelectedSignature(
                      event.target.value
                    )
                  }
                  style={{
                    width: '100%',
                    minHeight: 40,
                    padding: '7px 8px',
                    boxSizing:
                      'border-box',
                  }}
                >
                  <option value="">
                    Select provider
                    signature
                  </option>

                  {availableSignatures.map(
                    (item) => (
                      <option
                        key={
                          item.providerSignatureId
                        }
                        value={
                          item.providerSignatureId
                        }
                      >
                        {formatProviderSignature(
                          item
                        )}
                      </option>
                    )
                  )}
                </select>

                {overrideLoading && (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 11,
                      color: '#666',
                    }}
                  >
                    Loading active
                    provider signatures…
                  </div>
                )}

                {/* =============================================
                    MANAGER / ADMIN OVERRIDE TOGGLE
                    ============================================= */}
                {canUseManagerOverride && (
                  <label
                    style={{
                      display: 'flex',
                      alignItems:
                        'flex-start',
                      gap: 8,
                      marginTop: 12,
                      padding: 9,
                      border:
                        '1px solid #ddd',
                      borderRadius: 7,
                      background:
                        overrideMode
                          ? '#fff8df'
                          : '#fafafa',
                      fontSize: 12,
                      lineHeight: 1.4,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={
                        overrideMode
                      }
                      disabled={
                        disabled ||
                        signing ||
                        overrideLoading
                      }
                      onChange={(
                        event
                      ) =>
                        changeOverrideMode(
                          event.target
                            .checked
                        )
                      }
                    />

                    <span>
                      <strong>
                        Manager/Admin
                        provider override
                      </strong>

                      <br />

                      Allow selection of
                      an active signature
                      belonging to a
                      different provider
                      when a legitimate
                      override is
                      required.
                    </span>
                  </label>
                )}
              </div>
            </section>

            {/* ===============================================
                SELECTED SIGNATURE DETAILS
                =============================================== */}
            {selectedSignature && (
              <section
                style={{
                  marginTop: 13,
                  border:
                    providerMismatch
                      ? '1px solid #d6a100'
                      : '1px solid #a8d5b0',
                  borderRadius: 9,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    padding:
                      '9px 11px',
                    background:
                      providerMismatch
                        ? '#fff8df'
                        : '#f4fbf5',
                    borderBottom:
                      '1px solid #ddd',
                  }}
                >
                  <strong>
                    Selected Signature
                  </strong>

                  {providerMismatch && (
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 12,
                        color:
                          '#795500',
                      }}
                    >
                      This signature
                      belongs to a
                      different provider
                      and requires an
                      authorized
                      Manager/Admin
                      override.
                    </div>
                  )}
                </div>

                <div
                  style={{
                    padding: 11,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                    }}
                  >
                    {displayValue(
                      selectedSignature
                        .providerName,
                      'Provider'
                    )}

                    {selectedSignature
                      .credentials
                      ? `, ${selectedSignature.credentials}`
                      : ''}
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns:
                        'repeat(auto-fit, minmax(160px, 1fr))',
                      gap: 8,
                      marginTop: 9,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          textTransform:
                            'uppercase',
                          color: '#777',
                          fontWeight: 700,
                        }}
                      >
                        Provider ID
                      </div>

                      <div
                        style={{
                          marginTop: 3,
                          fontSize: 13,
                        }}
                      >
                        {displayValue(
                          selectedSignature
                            .providerId
                        )}
                      </div>
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          textTransform:
                            'uppercase',
                          color: '#777',
                          fontWeight: 700,
                        }}
                      >
                        License
                      </div>

                      <div
                        style={{
                          marginTop: 3,
                          fontSize: 13,
                        }}
                      >
                        {selectedSignature
                          .licenseState
                          ? `${selectedSignature.licenseState} `
                          : ''}

                        {displayValue(
                          selectedSignature
                            .licenseNumber
                        )}
                      </div>
                    </div>

                    <div>
                      <div
                        style={{
                          fontSize: 10,
                          textTransform:
                            'uppercase',
                          color: '#777',
                          fontWeight: 700,
                        }}
                      >
                        Signature Image
                      </div>

                      <div
                        style={{
                          marginTop: 3,
                          fontSize: 13,
                        }}
                      >
                        {selectedSignature
                          .signatureImageReference
                          ? 'Configured'
                          : 'Missing'}
                      </div>
                    </div>
                  </div>

                  {selectedSignature
                    .signaturePrefixText && (
                    <div
                      style={{
                        marginTop: 9,
                        fontSize: 12,
                      }}
                    >
                      <strong>
                        Signature Prefix:
                      </strong>{' '}
                      {
                        selectedSignature
                          .signaturePrefixText
                      }
                    </div>
                  )}

                  {selectedSignature
                    .requiredSignatureText && (
                    <div
                      style={{
                        marginTop: 9,
                        borderTop:
                          '1px solid #ddd',
                        paddingTop: 8,
                        fontSize: 12,
                        lineHeight: 1.45,
                        whiteSpace:
                          'pre-wrap',
                      }}
                    >
                      {
                        selectedSignature
                          .requiredSignatureText
                      }
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* ===============================================
                MANAGER OVERRIDE REASON
                =============================================== */}
            {providerMismatch &&
              canUseManagerOverride && (
              <div
                style={{
                  marginTop: 13,
                }}
              >
                <label
                  htmlFor="clinical-signature-override-reason"
                  style={{
                    display: 'block',
                    marginBottom: 5,
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  Manager/Admin Override
                  Reason
                  <span
                    aria-hidden="true"
                    style={{
                      marginLeft: 4,
                      color: '#b42318',
                    }}
                  >
                    *
                  </span>
                </label>

                <textarea
                  id="clinical-signature-override-reason"
                  value={
                    managerOverrideReason
                  }
                  maxLength={
                    MAX_OVERRIDE_REASON_LENGTH
                  }
                  disabled={
                    disabled ||
                    signing
                  }
                  rows={4}
                  placeholder="Explain why a signature belonging to a different provider must be used."
                  onChange={(event) => {
                    setManagerOverrideReason(
                      event.target.value
                    );

                    setError('');
                  }}
                  style={{
                    width: '100%',
                    boxSizing:
                      'border-box',
                    minHeight: 90,
                    resize: 'vertical',
                    padding: 9,
                    fontFamily:
                      'inherit',
                    fontSize: 13,
                    lineHeight: 1.45,
                  }}
                />

                <div
                  style={{
                    marginTop: 4,
                    textAlign: 'right',
                    fontSize: 10,
                    color:
                      overrideReasonLength >=
                      1900
                        ? '#8a6500'
                        : '#777',
                  }}
                >
                  {
                    overrideReasonLength
                  }
                  /
                  {
                    MAX_OVERRIDE_REASON_LENGTH
                  }
                </div>

                <div
                  style={{
                    marginTop: 5,
                    fontSize: 11,
                    color: '#666',
                  }}
                >
                  This explanation is
                  internal audit
                  information and is not
                  part of the
                  insurer-facing
                  signature display.
                </div>
              </div>
            )}

            {/* ===============================================
                PERMANENT SIGN WARNING
                =============================================== */}
            <div
              style={{
                marginTop: 15,
                border:
                  '2px solid #b42318',
                borderRadius: 9,
                background:
                  '#fff5f5',
                padding: 12,
              }}
            >
              <strong
                style={{
                  color: '#8a1c13',
                }}
              >
                Permanent Signing
              </strong>

              <div
                style={{
                  marginTop: 5,
                  fontSize: 13,
                  lineHeight: 1.5,
                }}
              >
                Signing finalizes the
                clinical provider
                signature and locks this
                clinical note from
                normal editing.

                {' '}

                Review the clinical
                documentation, provider,
                license information, and
                date of service before
                continuing.
              </div>

              <label
                style={{
                  display: 'flex',
                  alignItems:
                    'flex-start',
                  gap: 8,
                  marginTop: 11,
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1.45,
                }}
              >
                <input
                  type="checkbox"
                  checked={
                    permanentConfirmation
                  }
                  disabled={
                    disabled ||
                    signing ||
                    !selectedSignature ||
                    !canSignAccordingToValidation
                  }
                  onChange={(
                    event
                  ) => {
                    setPermanentConfirmation(
                      event.target
                        .checked
                    );

                    setError('');
                  }}
                />

                <span>
                  I have reviewed this
                  clinical note and
                  understand that this
                  action permanently
                  signs and locks it.
                </span>
              </label>

              <div
                style={{
                  display: 'flex',
                  justifyContent:
                    'flex-end',
                  marginTop: 12,
                }}
              >
                <button
                  type="button"
                  disabled={
                    !canPressSign
                  }
                  onClick={
                    signClinicalNote
                  }
                >
                  {signing
                    ? 'Permanently Signing…'
                    : 'Permanently Sign Clinical Note'}
                </button>
              </div>
            </div>

            {disabled && (
              <div
                style={{
                  marginTop: 11,
                  fontSize: 12,
                  color: '#666',
                }}
              >
                Permanent signing is
                currently read only.
              </div>
            )}
          </>
        )}

        {note?.entityVersion !==
          null &&
          note?.entityVersion !==
            undefined && (
          <div
            style={{
              marginTop: 12,
              textAlign: 'right',
              fontSize: 10,
              color: '#999',
            }}
          >
            Clinical note version{' '}
            {note.entityVersion}
          </div>
        )}
      </div>
    </section>
  );
}